/**
 * ColorDistributionChart - Color distribution pie chart
 *
 * Displays mana pip counts per color (WUBRG + C) using Chart.js.
 */
import type { ChartOptions } from 'chart.js';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Pie } from 'react-chartjs-2';

import InfoButton, { InfoList, InfoSection, InfoTip, InfoWarning } from '@/components/InfoButton/InfoButton';
import type { Color } from '@/types/card';

import type { ColorDistribution } from '../../lib/deckStatistics';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

export interface ColorDistributionChartProps {
  data: ColorDistribution;
  /** Compact mode for left panel */
  compact?: boolean;
}

/** MTG mana colors matching the theme */
const MANA_COLORS: Record<Color | 'C', string> = {
  W: '#f9faf4', // White mana
  U: '#0e67ab', // Blue mana
  B: '#3d3d3d', // Black mana (slightly lighter for visibility)
  R: '#d3202a', // Red mana
  G: '#00733e', // Green mana
  C: '#ccc2c0', // Colorless
};

const COLOR_LABELS: Record<Color | 'C', string> = {
  W: 'White',
  U: 'Blue',
  B: 'Black',
  R: 'Red',
  G: 'Green',
  C: 'Colorless',
};

const COLOR_ORDER: (Color | 'C')[] = ['W', 'U', 'B', 'R', 'G', 'C'];

function ColorDistributionInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What is this?">
        <p>
          This chart counts <strong>mana pips</strong> — the colored symbols in your spells&apos; mana costs. It shows how color-intensive
          your deck is and whether your colors are balanced.
        </p>
      </InfoSection>

      <InfoSection title="How pips are counted">
        <InfoList
          items={[
            'Each colored symbol counts as 1 pip',
            'A card costing {1}{W}{W} contributes 2 white pips',
            'Generic mana ({1}, {2}, etc.) is not counted',
            'Colorless pips ({C}) are counted separately',
            'Lands are excluded from pip counting',
          ]}
        />
      </InfoSection>

      <InfoSection title="What the balance tells you">
        <InfoList
          items={[
            'Even split = You need equal mana sources of each color',
            'Heavy in one color = You need more sources of that color',
            'Splash color (small slice) = Fewer sources needed, but still some',
          ]}
        />
      </InfoSection>

      <InfoSection title="Using this with mana base">
        <p>
          Compare this chart to your land colors. If 60% of your pips are white but only 40% of your lands produce white, you&apos;ll
          struggle to cast your white spells on time.
        </p>
      </InfoSection>

      <InfoTip>
        Double-pip cards like {'{W}{W}'} or {'{U}{U}'} are much harder to cast in multicolor decks. If you&apos;re having mana issues,
        consider replacing double-pip spells with single-pip alternatives.
      </InfoTip>

      <InfoWarning>
        This shows mana <em>requirements</em>, not mana <em>production</em>. Check the Color Source Analysis in Land Analysis for whether
        you can actually cast these spells.
      </InfoWarning>
    </>
  );
}

export default function ColorDistributionChart({ data, compact = true }: ColorDistributionChartProps): ReactElement {
  const chartConfig = useMemo(() => {
    // Filter to colors with pips
    const activeColors = COLOR_ORDER.filter((color) => data.pips[color] > 0);

    return {
      labels: activeColors.map((color) => COLOR_LABELS[color]),
      datasets: [
        {
          data: activeColors.map((color) => data.pips[color]),
          backgroundColor: activeColors.map((color) => MANA_COLORS[color]),
          borderColor: '#1a1a1f',
          borderWidth: 2,
          hoverOffset: 4,
        },
      ],
    };
  }, [data.pips]);

  const options: ChartOptions<'pie'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: !compact,
          position: 'right' as const,
          labels: {
            color: '#a8a8b8',
            font: { size: 11 },
            boxWidth: 12,
            padding: 8,
          },
        },
        tooltip: {
          backgroundColor: '#1a1a1f',
          borderColor: '#2a2a32',
          borderWidth: 1,
          titleColor: '#e8e8ef',
          bodyColor: '#a8a8b8',
          padding: 8,
          displayColors: true,
          callbacks: {
            label: (item) => {
              const total = (item.dataset.data as number[]).reduce((a, b) => a + b, 0);
              const pct = total > 0 ? Math.round((Number(item.raw) / total) * 100) : 0;
              return ` ${item.raw} pip${Number(item.raw) !== 1 ? 's' : ''} (${pct}%)`;
            },
          },
        },
      },
    }),
    [compact],
  );

  // Don't render if no pips
  if (data.totalPips === 0) {
    return (
      <div className="text-ash flex h-25 items-center justify-center text-sm">
        <span>No mana pips</span>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <span className="text-ash text-xs">Mana Pips by Color</span>
        <InfoButton title="Color Distribution">
          <ColorDistributionInfo />
        </InfoButton>
      </div>
      <div className={compact ? 'h-25 w-full' : 'h-50 w-full'}>
        <Pie data={chartConfig} options={options} />
      </div>
    </div>
  );
}
